<template>
  <div class="main-content-container overflow-hidden">
    <PageTitle :pageTitle="isEditing ? 'Edit Blog Post' : 'Add Blog Post'" subTitle="Management" />

    <div class="card bg-white border-0 rounded-3 mb-4">
      <div class="card-body p-4">
        <form @submit.prevent="submitForm">
          <div class="row">
            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Title</label>
              <input type="text" class="form-control" v-model="form.title" placeholder="Enter post title" required @input="autoSlug" />
            </div>

            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Slug (URL Path)</label>
              <input type="text" class="form-control" v-model="form.slug" placeholder="e.g. bahaya-plagiarisme-skripsi" required />
            </div>

            <div class="col-lg-4 col-md-4 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Category</label>
              <input type="text" class="form-control" v-model="form.category" placeholder="e.g. Plagiarisme, Tutorial" required />
            </div>

            <div class="col-lg-4 col-md-4 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Publication Date</label>
              <input type="text" class="form-control" v-model="form.date" placeholder="e.g. 19 Juli 2026" required />
            </div>

            <div class="col-lg-4 col-md-4 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Read Time</label>
              <input type="text" class="form-control" v-model="form.read_time" placeholder="e.g. 5 menit baca" required />
            </div>

            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Material Icon</label>
              <div class="d-flex align-items-center gap-2 mb-2">
                <!-- Icon Live Preview -->
                <div class="rounded-3 border d-flex align-items-center justify-content-center bg-light text-primary" style="width: 45px; height: 45px; flex-shrink: 0;">
                  <span class="material-symbols-outlined fs-24">{{ form.icon || 'help' }}</span>
                </div>
                <!-- Custom Icon Name Input -->
                <input type="text" class="form-control" v-model="form.icon" placeholder="Type icon name or click preset below" required />
              </div>
              <!-- Presets Grid -->
              <div class="d-flex gap-1 flex-wrap">
                <button type="button" v-for="ico in iconPresets" :key="ico" class="btn btn-sm btn-outline-secondary fs-12 py-1 px-2 d-flex align-items-center gap-1" @click="form.icon = ico">
                  <span class="material-symbols-outlined fs-14">{{ ico }}</span>
                  <span>{{ ico }}</span>
                </button>
              </div>
            </div>

            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium d-flex justify-content-between">
                <span>Card Background Gradient</span>
                <span class="text-muted fs-12">Click presets below or type custom CSS</span>
              </label>
              <input type="text" class="form-control mb-2" v-model="form.bg" placeholder="linear-gradient(135deg,#5f35f5,#7b5cf8)" required />
              <div class="d-flex gap-1 flex-wrap">
                <button type="button" v-for="p in bgPresets" :key="p.name" class="btn btn-sm text-white fs-12 border-0" :style="{ background: p.gradient }" @click="form.bg = p.gradient" style="padding: 2px 6px;">
                  {{ p.name }}
                </button>
              </div>
            </div>

            <div class="col-lg-12 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Excerpt (Short description for listing)</label>
              <textarea class="form-control" v-model="form.excerpt" rows="2" placeholder="Enter short summary..." required></textarea>
            </div>

            <div class="col-lg-12 col-md-12 mb-4">
              <div class="form-check form-switch mt-2">
                <input class="form-check-input" type="checkbox" id="isActive" v-model="form.is_active" />
                <label class="form-check-label text-dark fs-14 fw-medium" for="isActive">Active Status (Visible to users)</label>
              </div>
            </div>

            <!-- Content Paragraphs Editor -->
            <div class="col-lg-12 col-md-12 mb-4">
              <label class="form-label text-dark fs-14 fw-medium d-flex justify-content-between align-items-center">
                <span>Content Paragraphs</span>
                <button type="button" class="btn btn-sm btn-outline-primary py-1 px-2" @click="addParagraph">
                  <i class="ri-add-line me-1"></i>Add Paragraph
                </button>
              </label>
              <div v-for="(p, index) in form.content" :key="index" class="d-flex mb-3 align-items-start gap-2">
                <span class="text-secondary fw-bold mt-2" style="width: 30px;">#{{ index + 1 }}</span>
                <textarea class="form-control" v-model="form.content[index]" rows="3" placeholder="Enter paragraph content..." required></textarea>
                <button type="button" class="btn btn-outline-danger p-2 lh-1 mt-1" @click="removeParagraph(index)" :disabled="form.content.length <= 1">
                  <i class="material-symbols-outlined fs-18">delete</i>
                </button>
              </div>
            </div>
          </div>
          
          <div class="d-flex justify-content-end gap-2">
            <RouterLink to="/blog-posts" class="btn btn-secondary fs-15 fw-medium">Cancel</RouterLink>
            <button type="submit" class="btn btn-primary fs-15 fw-medium" :disabled="loading">
              <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {{ isEditing ? 'Update Post' : 'Create Post' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import PageTitle from '@/components/Common/PageTitle.vue';
import blogPostService from '@/services/blogPostService';

export default defineComponent({
  name: 'BlogPostFormPage',
  components: {
    PageTitle
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    
    const isEditing = ref(false);
    const postId = ref<number | null>(null);
    const loading = ref(false);

    const bgPresets = [
      { name: 'Blue', gradient: 'linear-gradient(135deg,#5f35f5,#7b5cf8)' },
      { name: 'Red', gradient: 'linear-gradient(135deg,#e74c3c,#c0392b)' },
      { name: 'Green', gradient: 'linear-gradient(135deg,#27ae60,#16a085)' },
      { name: 'Orange', gradient: 'linear-gradient(135deg,#f5a623,#e67e22)' },
      { name: 'Purple', gradient: 'linear-gradient(135deg,#8e44ad,#6c3483)' },
      { name: 'Teal', gradient: 'linear-gradient(135deg,#2980b9,#1a6fa3)' }
    ];

    const iconPresets = [
      'warning', 'search', 'psychology', 'edit_note', 'article',
      'compare', 'star', 'school', 'settings', 'menu_book',
      'info', 'verified'
    ];

    // Helper to format today's date into Indonesian format
    const getIndonesianTodayDate = () => {
      const months = [
        'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
        'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
      ];
      const today = new Date();
      const date = today.getDate();
      const month = months[today.getMonth()];
      const year = today.getFullYear();
      return `${date} ${month} ${year}`;
    };

    const form = ref({
      slug: '',
      title: '',
      excerpt: '',
      content: [''] as string[],
      category: '',
      date: getIndonesianTodayDate(),
      read_time: '5 menit baca',
      icon: 'article',
      bg: 'linear-gradient(135deg,#5f35f5,#7b5cf8)',
      is_active: true
    });

    onMounted(async () => {
      if (route.params.id) {
        isEditing.value = true;
        postId.value = Number(route.params.id);
        await fetchPost(postId.value);
      }
    });

    const autoSlug = () => {
      if (isEditing.value) return;
      form.value.slug = form.value.title
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '') // remove invalid chars
        .replace(/\s+/g, '-') // replace space with -
        .replace(/-+/g, '-'); // collapse multiple dashes
    };

    const fetchPost = async (id: number) => {
      try {
        const response = await blogPostService.getById(id);
        const data = response.data;
        form.value = {
          slug: data.slug,
          title: data.title,
          excerpt: data.excerpt,
          content: (data.content && data.content.length > 0) ? [...data.content] : [''],
          category: data.category,
          date: data.date,
          read_time: data.read_time,
          icon: data.icon,
          bg: data.bg,
          is_active: !!data.is_active
        };
      } catch (error) {
        console.error('Error fetching blog post:', error);
        router.push('/blog-posts');
      }
    };

    const addParagraph = () => {
      form.value.content.push('');
    };

    const removeParagraph = (index: number) => {
      if (form.value.content.length > 1) {
        form.value.content.splice(index, 1);
      }
    };

    const submitForm = async () => {
      loading.value = true;
      try {
        const payload = {
          ...form.value,
          content: form.value.content.filter(p => p.trim() !== '')
        };

        if (isEditing.value && postId.value) {
          await blogPostService.update(postId.value, payload);
        } else {
          await blogPostService.create(payload);
        }
        router.push('/blog-posts');
      } catch (error: any) {
        console.error('Error saving blog post:', error);
        if (error.response?.data?.message) {
          alert(error.response.data.message);
        } else {
          alert('Failed to save blog post.');
        }
      } finally {
        loading.value = false;
      }
    };

    return {
      isEditing,
      form,
      loading,
      bgPresets,
      iconPresets,
      autoSlug,
      addParagraph,
      removeParagraph,
      submitForm
    };
  }
});
</script>
